"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "@auth0/auth0-spa-js";
import { usePathname, useRouter } from "next/navigation";
import {
  handleAuthCallback,
  getUserProfile,
  getAccessToken,
  login,
  logout,
  isAuthenticated as checkAuthenticated,
} from "./auth0-client";
import { getApiBaseUrl, isProtectedPath } from "./auth0-config";

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string>;
  error: Error | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Primary authentication provider component that:
 * 1. Handles authentication state
 * 2. Provides authentication methods via context
 * 3. Integrates with Auth0
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if we're in the callback page with code and state parameters
        if (
          typeof window !== "undefined" &&
          window.location.search &&
          window.location.search.includes("code=") &&
          window.location.search.includes("state=")
        ) {
          // Don't handle errors here - let the callback page handle them
          if (window.location.search.includes("error=")) {
            setIsLoading(false);
            return;
          }

          try {
            const authResult = await handleAuthCallback();
            if (authResult?.user) {
              setUser(authResult.user);
              setIsAuthenticated(true);

              // Sync with backend
              try {
                const response = await fetch(
                  `${getApiBaseUrl()}/api/auth0-sync`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${authResult.token}`,
                    },
                    body: JSON.stringify({
                      auth0_id: authResult.user.sub,
                      email: authResult.user.email,
                      name:
                        authResult.user.name ||
                        authResult.user.nickname ||
                        "User",
                    }),
                  }
                );

                if (response.ok) {
                  const userData = await response.json();
                  console.log("User synced with backend:", userData);

                  // If we have a returnTo path, redirect there
                  if (authResult.returnTo) {
                    window.location.href = authResult.returnTo;
                  }
                }
              } catch (err) {
                console.error("Failed to sync user with backend:", err);
              }

              // Remove the query parameters to clean up the URL
              window.history.replaceState(
                {},
                document.title,
                window.location.pathname
              );
            }
          } catch (callbackError) {
            console.error("Error handling callback:", callbackError);
            setError(
              callbackError instanceof Error
                ? callbackError
                : new Error("Authentication callback failed")
            );
          }
        } else {
          // Check if user is already authenticated
          try {
            const isUserAuthenticated = await checkAuthenticated();

            if (isUserAuthenticated) {
              const userProfile = await getUserProfile();
              if (userProfile) {
                setUser(userProfile);
                setIsAuthenticated(true);
              }
            }
          } catch (authCheckError) {
            console.log("Not authenticated:", authCheckError);
            // This is not necessarily an error - user might just not be logged in
          }
        }
      } catch (err) {
        console.error("Authentication error:", err);
        setError(err instanceof Error ? err : new Error("Unknown error"));
      } finally {
        setIsLoading(false);
      }
    };

    if (typeof window !== "undefined") {
      initAuth();
    }
  }, []);

  const getToken = async (): Promise<string> => {
    try {
      return await getAccessToken();
    } catch (err) {
      console.error("Error getting access token:", err);
      throw err;
    }
  };

  const authContextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login: async (returnTo?: string) => {
      try {
        await login(returnTo);
      } catch (err) {
        console.error("Login error:", err);
        setError(err instanceof Error ? err : new Error("Login failed"));
      }
    },
    logout: async () => {
      try {
        await logout();
        setIsAuthenticated(false);
        setUser(null);
      } catch (err) {
        console.error("Logout error:", err);
        setError(err instanceof Error ? err : new Error("Logout failed"));
      }
    },
    getToken,
    error,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access the auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Component that protects routes requiring authentication
 * This component handles both the auth check and conditional redirects
 */
export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [authorized, setAuthorized] = useState(true); // Default to authorized
  const [checkingAuth, setCheckingAuth] = useState(true);
  const router = useRouter();
  const pathnameOrNull = usePathname();
  const pathname = pathnameOrNull || "/";

  useEffect(() => {
    // Only check auth for protected paths
    if (!isProtectedPath(pathname)) {
      setCheckingAuth(false);
      setAuthorized(true);
      return;
    }

    // For protected paths, check if user is authenticated
    if (!isLoading) {
      if (!isAuthenticated) {
        console.log(
          "Protected route requires authentication, redirecting to login"
        );
        router.push(`/login?returnTo=${encodeURIComponent(pathname)}`);
        setAuthorized(false);
      } else {
        setAuthorized(true);
      }
      setCheckingAuth(false);
    }
  }, [isAuthenticated, isLoading, pathname, router]);

  // Show nothing while checking authentication
  if (isLoading || checkingAuth) {
    return null;
  }

  // If authorized, render children
  return authorized ? <>{children}</> : null;
}
